<template>
  <div class="relative z-1">
    <saving-overlay
      :label="i18n.saving"
      v-if="saving"
      class="rounded-r rounded-bl"
    ></saving-overlay>
    <form @submit.prevent="save" class="p-5">
      <div class="pb-5">
        <font-awesome-icon icon="spinner" spin v-if="!tokens" fixed-width />
        <div v-else>
          <table class="w-full mb-4" v-if="tokens.tokens.length">
            <thead>
              <tr class="text-sm text-gray-700">
                <td class="pl-4" v-text="i18nToken.thead[0]"></td>
                <td v-text="i18nToken.thead[1]"></td>
                <td v-text="i18nToken.thead[2]"></td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="t in tokens.tokens"
                :key="t.prefix"
                class="even:bg-gray-200"
                :class="t.is_expired ? 'text-gray-700' : ''"
              >
                <td
                  class="p-1 pl-4 text-green-800"
                  v-if="plainTextToken.startsWith(t.prefix)"
                  v-text="plainTextToken"
                ></td>
                <td class="p-1 pl-4" v-else>
                  {{ t.prefix
                  }}<span class="text-gray-700 font-mono">******</span>
                </td>
                <td class="p-1">{{ t.name }}</td>
                <td
                  class="p-1"
                  :class="t.is_expired ? 'text-red-800 font-bold italic' : ''"
                >
                  {{ t.expired_at.substr(0, 10) }}
                </td>
                <td class="p-1">
                  <button
                    class="button button-sm warning border border-red-800 float-right"
                    type="button"
                    @click="disableToken(t)"
                    v-text="i18nToken.revoke"
                  ></button>
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="plainTextToken"
            class="p-4 mb-4 border-green-200 bg-green-100 text-green-800"
          >
            <text-copier
              :message="plainTextToken"
              class="text-sm"
            ></text-copier>
            {{ i18nToken.plain_text_created }}
          </div>

          <div>
            <details ref="newTokenDetails" :open="tokens.tokens.length == 0">
              <summary>
                {{
                  tokens.tokens.length == 0
                    ? i18nToken.form.summary
                    : i18nToken.form.summary_add
                }}
              </summary>
              <form
                class="p-4 bg-gray-100 rounded"
                @submit.prevent="createToken"
              >
                <label class="block pb-4">
                  <span v-text="i18nToken.form.name"></span>
                  <input type="text" v-model="newToken.name" />
                </label>
                <fieldset class="pb-4">
                  <legend v-text="i18nToken.form.abilities"></legend>
                  <label
                    v-for="a in tokens.abilities"
                    :key="a"
                    class="flex items-baseline font-mono"
                  >
                    <span
                      ><input
                        type="checkbox"
                        v-model="newToken.abilities"
                        class="inline-block w-4 h-4 appearance-auto mr-2"
                        :value="a"
                    /></span>
                    {{ a }}
                  </label>
                </fieldset>

                <label class="block pb-4">
                  <span v-text="i18nToken.form.expired_at"></span>
                  <input type="date" v-model="newToken.expired_at" />
                </label>
                <button
                  class="button button-base action border"
                  type="submit"
                  v-text="i18nToken.form.button"
                ></button>
              </form>
            </details>
          </div>
          <div
            v-if="tokens.legacy"
            class="border-blue-200 bg-blue-100 text-blue-800 rounded border p-4 mt-4"
          >
            <p>
              {{ i18nToken.legacy.warning }}
            </p>
            <button
              class="button button-sm warning border border-red-800 my-4"
              type="button"
              @click="disableLegacy"
              v-text="i18nToken.legacy.revoke"
            ></button>
            <text-copier :message="tokens.legacy" class="text-sm"></text-copier>
          </div>
        </div>
      </div>
      <div class="py-5 text-sm">
        <div
          class="border-b border-gray-100 pb-2 mb-2 font-bold text-center"
          v-html="i18n.cloudflare.title"
        ></div>
        <p v-html="i18n.cloudflare.info"></p>
      </div>
      <div class="pb-5">
        <simple-label
          id="upload_via_cf"
          :label="i18n.upload_via_cf.label"
          label-info=""
          :required="false"
          class="border-b border-gray-100 mb-2"
        ></simple-label>
        <simple-checkbox
          :label="i18n.upload_via_cf.detail"
          v-model="form.upload_via_cf"
          name="upload_via_cf"
          :with-error-message="false"
        ></simple-checkbox>
      </div>
      <div class="pb-5">
        <simple-label
          id="download_via_cf"
          :label="i18n.download_via_cf.label"
          label-info=""
          :required="false"
          class="border-b border-gray-100 mb-2"
        ></simple-label>
        <simple-checkbox
          :label="i18n.download_via_cf.detail"
          v-model="form.download_via_cf"
          name="download_via_cf"
          :with-error-message="false"
        ></simple-checkbox>
      </div>

      <div class="flex items-center justify-end">
        <ranch-button
          type="submit"
          :label="i18n.save"
          icon="save"
        ></ranch-button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import { flash, handleAxiosError } from "@/boot/functions";

import { Form } from "../../../Utilities/Form";
import { mapState } from "vuex";
import SimpleCheckbox from "../../../Design/Forms/SimpleCheckbox";
import SavingOverlay from "../../../Design/Forms/SavingOverlay";
import SimpleLabel from "../../../Design/Forms/SimpleLabel";
import TextCopier from "../../../Design/TextCopier";

export default {
  props: ["options"],
  components: { SimpleCheckbox, SavingOverlay, SimpleLabel, TextCopier },
  data() {
    return {
      saving: false,
      saved: false,
      tokens: null,
      plainTextToken: "",
      i18nToken: null,
      newToken: {
        name: "",
        abilities: [],
        expired_at: "",
      },
      form: new Form({
        upload_via_cf: this.options.upload_via_cf,
        download_via_cf: this.options.download_via_cf,
      }),
    };
  },
  methods: {
    save() {
      this.saving = true;
      this.form
        .post(this.endpoint)
        .then((data) => {
          this.$store.commit("account/setTransfer", data.data);
          this.saving = false;
          this.saved = true;
          flash(this.i18n.saved, "success");
        })
        .catch(handleAxiosError)
        .then(() => {
          this.saving = false;
        });
    },
    disableLegacy() {
      axios
        .delete("/api/app/legacy-api-key")
        .then(() => {
          this.tokens.legacy = "";
        })
        .catch(handleAxiosError);
    },
    setTokenData({ data, i18n }) {
      this.i18nToken = i18n;
      this.tokens = data;
      this.plainTextToken = "";
      this.newToken.name = "";
      this.newToken.abilities = data.abilities;
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      this.newToken.expired_at = d.toISOString().substr(0, 10);
    },
    createToken() {
      console.log(this.newToken);
      axios
        .post("/api/app/tokens", this.newToken)
        .then(({ data }) => {
          this.setTokenData(data);
          this.plainTextToken = data.plain_text;
          this.$refs.newTokenDetails.open = false;
        })
        .catch(handleAxiosError);
    },
    disableToken(t) {
      axios
        .delete(t.revoke_link)
        .then(({ data }) => {
          this.setTokenData(data);
        })
        .catch(handleAxiosError);
    },
  },
  computed: {
    ...mapState({
      i18n: (state) => state.account.i18n.settings.transfer.form,
    }),

    endpoint() {
      return this.$store.state.account.routes.transfer;
    },
  },
  activated() {
    // mounted is called only once because of keep-alive:
    axios
      .get("/api/app/tokens")
      .then(({ data }) => {
        this.setTokenData(data);
      })
      .catch(handleAxiosError);
  },
};
</script>
